/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardExtra } from '../../../Shared/Card'
import { CardTitle } from '../../../Shared/CardTitle'
import { Icon, Image } from 'semantic-ui-react'
import { Link } from 'react-router-dom-v5-compat'
import { useDeviceMediaQuery } from '../../../../Shared/UseDeviceMediaQuery/useDeviceMediaQuery'
import { useQuery } from '@tanstack/react-query'
import { isProviderAPos } from '../../../../../services/Provider/Provider.service'
import { TIME } from '../../../../../services/date.service'
import useUser from '../../../../Shared/UserProvider/useUser'
import { getCurrentMarkupsSummary } from '../../../../../services/PointOfSale/PointOfSale.service'
import { LoadingPlaceholderCardContent } from '../../../Shared/LoadingPlaceholderCardContent'


const computePriceIncrease = (markup) => {
    const increaseValue = Math.fround((100 * markup) / (100 - markup))
    return `${increaseValue < 0 ? '-' : '+'}${increaseValue.toFixed(2)}`
}

const isMarkupRange = (markup) => {
    return typeof markup === 'object' && markup !== null && 'min' in markup && 'max' in markup
}

const formatMarkupValue = (markup) => {
    if (isMarkupRange(markup)) {
        return `${markup.min}% - ${markup.max}%`
    }
    return `${markup}%`
}

const computePriceIncreaseDisplay = (markup) => {
    if (isMarkupRange(markup)) {
        const minIncrease = computePriceIncrease(markup.min)
        const maxIncrease = computePriceIncrease(markup.max)
        return `${minIncrease}% - ${maxIncrease}%`
    }
    return `${computePriceIncrease(markup)}%`
}

const MarkupsCard = () => {
    const [t] = useTranslation()
    const {user, can} = useUser()
    const {isMobile} = useDeviceMediaQuery()


    const {data, isLoading} = useQuery({
        queryKey: ['current-markups-summary', user.provider.object.id],
        queryFn: () => getCurrentMarkupsSummary(user.provider.object.id),
        staleTime: TIME.HOUR_IN_MS,
        enabled: isProviderAPos(user.provider),
    })

    const moreThan3Items = data?.platforms.length > 3

    return (
      <Card
        css={css`
            border-top: 2px solid ${isLoading ? 'unset' : 'grey'};

            .card-content {
                width: ${isMobile ? '340px' : '360px'};
                height: 416px;
            }
        `}
      >
          {isLoading ? (
            <LoadingPlaceholderCardContent/>
          ) : (
            <CardContent
              css={css`
                  display: flex;
                  flex-direction: column;
                  height: 100%;
              `}
            >
                <CardTitle
                  css={css`
                      margin-top: .25rem;
                  `}
                  title={t('dashboard.markup.title')}
                />

                <div
                  css={css`
                      padding: 12px;
                      display: flex;
                      flex-direction: column;
                      align-items: flex-start;
                      gap: 1rem;
                      justify-content: ${moreThan3Items ? 'space-between' : 'space-evenly'};
                      flex: 1;
                      height: 100%;
                  `}
                >
                    {data.platforms.map((platform) => {
                        const hasOneLinkId = platform.linkIds?.length === 1
                        return (
                          <div key={platform.module}
                               css={css`
                                   display: flex;
                                   justify-content: flex-start;
                                   align-items: center;
                                   gap: .5rem;
                               `}
                          >
                              <Image
                                src={`/img/platforms/${platform.module}.jpg`}
                                size="tiny"
                              />

                              <div
                                css={css`
                                    display: flex;
                                    flex-direction: column;
                                    height: 100%;
                                `}
                              >
                                  <div
                                    css={css`
                                        display: flex;
                                        align-items: baseline;
                                        gap: 0.5rem;
                                    `}
                                  >
                                      {platform.markups.main ? (
                                        <div>
                                            <div>
                                              <span
                                                css={css`
                                                    font-weight: 600;
                                                    font-size: 1.2em;
                                                `}
                                              >
                                        {t('dashboard.markup.markup_value', {value: formatMarkupValue(platform.markups.main)})}
                                    </span>
                                            </div>

                                            <p
                                              css={css`
                                                  font-size: 0.85em;
                                                  color: rgba(0, 0, 0, 0.6);
                                              `}
                                            >
                                                {t('dashboard.markup.markup_equivalent_in_price_increase', {value: computePriceIncreaseDisplay(platform.markups.main)})}
                                            </p>
                                        </div>
                                      ) : (
                                        <strong>{t('dashboard.markup.no_markup')}</strong>
                                      )}
                                  </div>

                                  {(hasOneLinkId && can('STATIC', 'categories')) && (
                                    <Link
                                      to={{
                                          pathname: `/categories/publications/${platform.linkIds[0]}/markups`,
                                          replace: true,
                                      }}
                                      css={css`
                                          text-decoration: none;
                                          font-size: 0.85rem;
                                      `}
                                    >
                                        {t('dashboard.markup.configure')}
                                    </Link>
                                  )}
                              </div>
                          </div>
                        )
                    })}
                </div>
            </CardContent>
          )}

          <CardExtra>
              <Link
                to={{
                    pathname: '/categories/publications',
                }}>
                  {t('category_set.manage_link')}
                  <Icon name="arrow right"/>
              </Link>
          </CardExtra>
      </Card>
    )
}

export { MarkupsCard }
